import { computed } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { navItem } from '@/navigation/vertical'

// DEVAMS-2670: default favorites-rail contents for users who have not yet
// pinned anything via the existing DEVAMS-2663/2667 customize-menu. 'Phone
// list' is not part of the main nav item list (it's only linked from the
// mobile bottom Footer today), so it's defined inline here rather than
// looked up from src/navigation/vertical/index.ts.
const DEFAULT_RAIL_ITEMS: navItem[] = [
  { title: 'Dashboard', icon: { icon: 'mdi-view-dashboard' }, to: { name: 'index' } },
  { title: 'Your task list', icon: { icon: 'mdi-format-list-bulleted' }, to: { name: 'yourTasks' } },
  { title: 'Your roster', icon: { icon: 'mdi-calendar-account' }, to: { name: 'roster' }, anyOf: [{ permission: 'roster' }] },
  { title: 'Phone list', icon: { icon: 'mdi-phone' }, to: { name: 'phoneList' } },
  { title: 'Add work', icon: { icon: 'mdi-account-hard-hat' }, to: { name: 'addWork' } },
]

// DEVAMS-2670 second-round review: the phone bottom tab bar (Footer.vue)
// reuses this same default-resolution logic but with 'Phone list' dropped
// (per review feedback — the module stays, it's just not in the default nav
// set anymore) and a fixed 4-slot cap to match the bottom bar's existing
// 4-button layout. Derived from DEFAULT_RAIL_ITEMS rather than a separate
// literal array so there is a single source of truth for the default set.
const BOTTOM_BAR_DEFAULT_ITEMS: navItem[] = DEFAULT_RAIL_ITEMS.filter(item => item.to.name !== 'phoneList')

/**
 * Resolves the items shown in the always-visible favorites rail (tablet /
 * web): the user's pinned items if any exist, otherwise the fixed
 * DEFAULT_RAIL_ITEMS set.
 *
 * DEVAMS-2670
 */
export function useNavFavoritesRail(pinnedNavItems: Ref<navItem[]> | ComputedRef<navItem[]>): ComputedRef<navItem[]> {
  return computed<navItem[]>(() => pinnedNavItems.value.length > 0 ? pinnedNavItems.value : DEFAULT_RAIL_ITEMS)
}

/**
 * Resolves the items shown in the phone bottom tab bar: the user's pinned
 * items (capped to 4, since the bar has a fixed 4-slot layout) if any exist,
 * otherwise BOTTOM_BAR_DEFAULT_ITEMS. Reuses the same pinned/default
 * resolution as useNavFavoritesRail rather than duplicating it.
 *
 * DEVAMS-2670 second-round review
 */
export function useNavBottomBarItems(pinnedNavItems: Ref<navItem[]> | ComputedRef<navItem[]>): ComputedRef<navItem[]> {
  return computed<navItem[]>(() => {
    const items = pinnedNavItems.value.length > 0 ? pinnedNavItems.value : BOTTOM_BAR_DEFAULT_ITEMS
    return items.slice(0, 4)
  })
}
